// ============================================================================
// Authentication provider for the **Carrier** realm.
//
// Responsibilities:
//   - Read + persist JWT + optional ExpiresUtc into localStorage
//   - Validate token structure + expiration
//   - Expose: { token, claims, isAuthenticated, loading, login(), logout() }
//   - Automatically keeps session alive across reloads and browser tabs
//
// Requirements enforced:
//   1) `role = "Carrier"` must exist in JWT claims
//   2) Session expiration prefers ExpiresUtc field, fallback to "exp" (JWT)
//   3) `logout()` clears only carrier realm data
//
// NOTE: The provider only manages *in-memory validity*. Navigation is performed
//       by the caller (TabsHomePage, Router guards, logout button, etc.).
// ============================================================================

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { STORAGE_CARRIER } from './storageKeys';
import { parseJwt } from './jwt';

const CarrierAuthContext = createContext(null);

// ============================================================================
// Hook access — consumers call this inside components
// ============================================================================
export function useCarrierAuth() {
  const ctx = useContext(CarrierAuthContext);
  if (!ctx) {
    throw new Error('useCarrierAuth must be used within <CarrierAuthProvider />');
  }
  return ctx;
}

// ============================================================================
// Verify expiration for the carrier session
// 1) Prefer ExpiresUtc from login payload
// 2) Fallback to "exp" claim in JWT
//
// Returns:
//    true  → expired
//    false → NOT expired (valid / unknown)
// ============================================================================
function isCarrierExpired(token, expiresUtc) {
  // ---- (1) Prefer ExpiresUtc ------------------------------------------------
  if (expiresUtc) {
    const expiresMs = Date.parse(expiresUtc); // ISO → timestamp
    if (!Number.isNaN(expiresMs) && expiresMs <= Date.now()) {
      return true;
    }
  }

  // ---- (2) Fallback to exp in JWT ------------------------------------------
  const payload = parseJwt(token);
  if (payload && typeof payload.exp === 'number') {
    return payload.exp * 1000 <= Date.now();
  }

  return false;
}

// ============================================================================
// Provider
// ============================================================================
export default function CarrierAuthProvider({ children }) {
  // Load token + expiration from localStorage (if any)
  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_CARRIER.token);
    } catch {
      return null;
    }
  });

  const [expiresUtc, setExpiresUtc] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_CARRIER.expiresUtc);
    } catch {
      return null;
    }
  });

  // Parsed JWT claims (role, etc.)
  const [claims, setClaims] = useState(() => parseJwt(token));

  // When true → auth still hydrating (do not flicker protected screens)
  const [loading, setLoading] = useState(true);

  // ========================================================================
  // Validate token on mount and each time token/expiresUtc changes
  // Also listens to localStorage "storage" events → multi-tab sync
  // ========================================================================
  useEffect(() => {
    const payload = parseJwt(token);

    const expired =
      !token ||
      isCarrierExpired(token, expiresUtc) ||
      payload?.role !== 'Carrier';

    if (expired) {
      // Invalidate session
      try {
        localStorage.removeItem(STORAGE_CARRIER.token);
        localStorage.removeItem(STORAGE_CARRIER.expiresUtc);
      } catch { }

      setToken(null);
      setExpiresUtc(null);
      setClaims(null);
    } else {
      setClaims(payload);
    }

    setLoading(false);

    // Multi-tab sync
    const onStorage = (e) => {
      if (e.key === STORAGE_CARRIER.token) setToken(e.newValue);
      if (e.key === STORAGE_CARRIER.expiresUtc) setExpiresUtc(e.newValue);
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [token, expiresUtc]);

  // ========================================================================
  // Programmatic login
  // Called from carrier login page:
  //
  //    login(jwtToken, expiresUtcIso)
  //
  // Notes:
  //  - No request here; only storage + local state
  //  - "expiresUtcIso" may be null
  // ========================================================================
  const login = (jwtToken, expiresUtcIso = null) => {
    try {
      localStorage.setItem(STORAGE_CARRIER.token, jwtToken);
      if (expiresUtcIso) {
        localStorage.setItem(STORAGE_CARRIER.expiresUtc, expiresUtcIso);
      }
    } catch { }

    setToken(jwtToken);
    if (expiresUtcIso) {
      setExpiresUtc(expiresUtcIso);
    }
    setClaims(parseJwt(jwtToken));
  };

  // ========================================================================
  // Logout only clears carrier realm state.
  //
  // IMPORTANT:
  //   Navigation (ex: go to /carrier-inviting) is performed by caller.
  //
  // Options:
  //   - clearStorage (default: true)
  //       When true, clears all carrier-scoped persisted data (session/profile/menus/etc.)
  //       in addition to the auth token. This avoids cross-realm side effects without
  //       relying on localStorage.clear().
  //       When false, only clears the auth token/expiresUtc and in-memory state.
  // ========================================================================
  const logout = (options = { clearStorage: true }) => {
    const clearStorage = options?.clearStorage !== false;
    try {
      // Always clear the carrier auth token + expiration
      localStorage.removeItem(STORAGE_CARRIER.token);
      localStorage.removeItem(STORAGE_CARRIER.expiresUtc);

      // Optionally clear all other carrier realm persisted data
      if (clearStorage) {
        localStorage.removeItem(STORAGE_CARRIER.session);
        localStorage.removeItem(STORAGE_CARRIER.menus);
        localStorage.removeItem(STORAGE_CARRIER.permissions);
        localStorage.removeItem(STORAGE_CARRIER.profile);
        // Invite-flow helper keys (carrier realm)
        localStorage.removeItem("carrier_invite_pending");
        localStorage.removeItem("carrier_invite_token");
      }

    } catch { }
    // Always clear in-memory state
    setToken(null);
    setExpiresUtc(null);
    setClaims(null);
  };


  // ========================================================================
  // Memoized context value
  // ========================================================================
  const value = useMemo(
    () => ({
      token,
      claims,
      loading,
      isAuthenticated: !!token && claims?.role === 'Carrier',
      login,
      logout,
    }),
    [token, claims, loading]
  );

  return (
    <CarrierAuthContext.Provider value={value}>
      {children}
    </CarrierAuthContext.Provider>
  );
}
